import { useState, useEffect } from 'react';
import { useApp } from '@/lib/core/providers/appProvider';
import { ImageWithCrops } from '@/services/platform/api/model';
import { cancelReservation } from '@/services/platform/data/booking';
import { formatDate, formatTime, getFirstName, ImageMap, timePartIconFromTime } from '@/lib/core/utils';
import { BookingCancelSheet } from '@/components/shared/booking/actions/cancel-sheet';
import type { ConfirmationRequestProps } from '../types';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { ConfirmationBanner } from '@/components/shared/booking/common/confirmation/banner';

export function ConfirmationRequest({
  booking,
  reservation,
  onGoHome,
  onCancelRequest,
}: ConfirmationRequestProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(s => s.locale);
  const { user } = useApp() as { user?: { name?: string | null } };
  const setMainClassName = useUIStore(s => s.setMainClassName);
  const [cancelSheetOpen, setCancelSheetOpen] = useState(false);

  useEffect(() => {
    setMainClassName('pb-0!');
    return () => setMainClassName('');
  }, [setMainClassName]);

  const firstName = getFirstName(user?.name) ?? null;

  const venueImage = ImageMap(booking.venue?.image as ImageWithCrops, 'bookingDetailHero');
  const venueLogo = ImageMap(booking.venue?.logo as ImageWithCrops);
  const roomDisplay = booking.room ?? '';
  const guestDisplay = booking.guestCount !== null ? `${booking.guestCount} ${t('App.Labels.Guests')}` : '';

  const dateDisplay = booking.date ? formatDate(booking.date?.toDateString(), locale) : '';
  const timeDisplay = booking.time ? formatTime(booking.time) : '';
  const timeIcon = timePartIconFromTime(booking.time);

  return (
    <ConfirmationBanner
      image={venueImage}
      logo={venueLogo}
      firstName={firstName}
      title={t('App.Labels.BookingRequestConfirmationText')}
      rows={[
        [
          ...(dateDisplay ? [{ text: dateDisplay }] : []),
          ...(timeDisplay ? [{ icon: timeIcon, text: timeDisplay }] : []),
        ],
        [
          ...(roomDisplay ? [{ icon: 'building', text: roomDisplay }] : []),
          ...(guestDisplay ? [{ icon: 'group', text: guestDisplay }] : []),
        ],
      ]}
      subtitle={t('App.Labels.BookingRequestConfirmationWaiting')}
      actions={[
        { label: t('App.Labels.GoBackHome'), onClick: onGoHome, variant: 'secondary-outline' },
        {
          label: t('App.Labels.CancelBookingRequest'),
          onClick: () => setCancelSheetOpen(true),
          variant: 'primary-solid',
        },
      ]}
      className="-mt-[max(1rem,env(safe-area-inset-top))]!"
    >
      {
        <BookingCancelSheet
          open={cancelSheetOpen}
          onOpenChange={setCancelSheetOpen}
          onConfirmCancel={async () => {
            const id = reservation.publicGuid;
            if (!id) throw new Error('Missing reservation ID');
            const success = await cancelReservation(id);
            if (!success) throw new Error('Cancellation failed');
          }}
          onSuccess={onCancelRequest}
          headerTitle={t('App.Labels.Confirmation')}
          title={t('App.Labels.CancelBookingRequestConfirmTitle')}
          description={t('App.Labels.CancelBookingRequestConfirmText')}
          cancelLabel={t('App.Labels.CancelRequest')}
          keepLabel={t('App.Labels.KeepRequest')}
        />
      }
    </ConfirmationBanner>
  );
}
